import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, MessageCircle, Loader2, ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import usePageMeta from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAMPOS_SELECT = ["1 a 10", "11 a 50", "51 a 200", "201 a 500", "500+"];

const CANAIS = [
    {
        icon: MessageCircle,
        label: "WhatsApp",
        valor: "(11) 91675-1213",
        href: "https://wa.me/5511916751213",
        externo: true,
        testid: "contato-canal-whatsapp",
    },
    {
        icon: Mail,
        label: "E-mail",
        valor: "contato@smartbeneficios.com.br",
        href: "mailto:contato@smartbeneficios.com.br",
        externo: false,
        testid: "contato-canal-email",
    },
    {
        icon: Phone,
        label: "Telefone",
        valor: "(11) 91675-1213",
        href: "tel:+5511916751213",
        externo: false,
        testid: "contato-canal-telefone",
    },
];

export default function Contato() {
    usePageMeta(
        "Contato | Smart Benefícios",
        "Fale com a Smart Benefícios e descubra como simplificar a gestão de benefícios da sua empresa com planos a partir de R$ 99/mês."
    );

    const [form, setForm] = useState({
        nome: "", empresa: "", cnpj: "", email: "", telefone: "", whatsapp: "", colaboradores: "", mensagem: "",
    });
    const [enviando, setEnviando] = useState(false);

    const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

    const enviar = async (e) => {
        e.preventDefault();
        setEnviando(true);
        try {
            const { data } = await axios.post(`${API}/contato`, form);
            toast.success(data.mensagem || "Mensagem enviada com sucesso.");
            setForm({ nome: "", empresa: "", cnpj: "", email: "", telefone: "", whatsapp: "", colaboradores: "", mensagem: "" });
        } catch (err) {
            const detalhe = err?.response?.data?.detail;
            toast.error(typeof detalhe === "string" ? detalhe : "Não foi possível enviar. Tente novamente.");
        } finally {
            setEnviando(false);
        }
    };

    return (
        <section className="relative overflow-hidden bg-azul-50/60 pb-20 pt-[128px] md:pb-28 md:pt-[168px]" data-testid="pagina-contato">
            <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-[-8%] h-[420px] w-[420px] rounded-full bg-azul-100/70 blur-3xl" />
            <div className="container-x relative z-10">
                <div className="max-w-3xl">
                    <Reveal>
                        <span className="inline-flex items-center gap-2 rounded-full border border-azul-100 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-azul-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-laranja-500" />
                            Contato
                        </span>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-tinta sm:text-5xl lg:text-6xl" data-testid="contato-title">
                            Fale com a Smart
                        </h1>
                    </Reveal>
                    <Reveal delay={0.14}>
                        <p className="mt-5 text-base leading-relaxed text-tinta-medio sm:text-lg">
                            Preencha o formulário e descubra como simplificar a gestão de benefícios da sua empresa com planos a partir de <strong className="text-azul-700">R$ 99/mês</strong>.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-12 grid gap-8 lg:grid-cols-5">
                    {/* Canais */}
                    <div className="space-y-4 lg:col-span-2">
                        {CANAIS.map((c, i) => (
                            <Reveal key={c.label} delay={i * 0.07}>
                                <a
                                    href={c.href}
                                    target={c.externo ? "_blank" : undefined}
                                    rel={c.externo ? "noopener noreferrer" : undefined}
                                    data-testid={c.testid}
                                    className="group flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-suave transition-all duration-300 hover:-translate-y-0.5 hover:border-azul-300 hover:shadow-card"
                                >
                                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-azul-50 transition-colors duration-300 group-hover:bg-azul-100">
                                        <c.icon className="h-5 w-5 text-azul-600" />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-tinta-suave">{c.label}</p>
                                        <p className="mt-0.5 truncate font-display text-lg font-bold text-tinta">{c.valor}</p>
                                    </div>
                                    <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-slate-300 transition-all duration-300 group-hover:text-laranja-500" />
                                </a>
                            </Reveal>
                        ))}
                        <Reveal delay={0.24}>
                            <p className="rounded-2xl border border-azul-100 bg-white/70 p-4 text-xs leading-relaxed text-tinta-suave">
                                Prefere falar agora? Chame no WhatsApp ou envie um e-mail — a equipe da Smart responde o mais rápido possível.
                            </p>
                        </Reveal>
                    </div>

                    {/* Formulário */}
                    <Reveal delay={0.1} className="lg:col-span-3">
                        <form
                            onSubmit={enviar}
                            data-testid="contato-form"
                            className="rounded-[2rem] border border-slate-200/80 bg-white p-6 shadow-card md:p-9"
                        >
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className="field-label" htmlFor="f-nome">Nome *</label>
                                    <input id="f-nome" required value={form.nome} onChange={set("nome")} className="field" placeholder="Seu nome" data-testid="contato-input-nome" />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="f-empresa">Empresa *</label>
                                    <input id="f-empresa" required value={form.empresa} onChange={set("empresa")} className="field" placeholder="Nome da sua empresa" data-testid="contato-input-empresa" />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="f-cnpj">CNPJ *</label>
                                    <input id="f-cnpj" required value={form.cnpj} onChange={set("cnpj")} className="field" placeholder="00.000.000/0001-00" data-testid="contato-input-cnpj" />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="f-email">E-mail *</label>
                                    <input id="f-email" type="email" required value={form.email} onChange={set("email")} className="field" placeholder="voce@suaempresa.com.br" data-testid="contato-input-email" />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="f-telefone">Telefone *</label>
                                    <input id="f-telefone" type="tel" required value={form.telefone} onChange={set("telefone")} className="field" placeholder="(00) 0000-0000" data-testid="contato-input-telefone" />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="f-whatsapp">WhatsApp *</label>
                                    <input id="f-whatsapp" type="tel" required value={form.whatsapp} onChange={set("whatsapp")} className="field" placeholder="(00) 00000-0000" data-testid="contato-input-whatsapp" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="field-label" htmlFor="f-colaboradores">Número de colaboradores *</label>
                                    <select id="f-colaboradores" required value={form.colaboradores} onChange={set("colaboradores")} className="field" data-testid="contato-input-colaboradores">
                                        <option value="" disabled>Selecione uma faixa</option>
                                        {CAMPOS_SELECT.map((op) => (
                                            <option key={op} value={op}>{op}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="field-label" htmlFor="f-mensagem">Mensagem</label>
                                    <textarea id="f-mensagem" rows={4} value={form.mensagem} onChange={set("mensagem")} className="field resize-none" placeholder="Como podemos ajudar a sua empresa?" data-testid="contato-input-mensagem" />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={enviando}
                                data-testid="contato-botao-enviar"
                                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-azul-500 px-7 py-4 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                                {enviando ? "Enviando..." : "Enviar mensagem"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
